import { Box, Skeleton, useTheme } from "@mui/material";
import { memo } from "react";

/**
 * SkeletonLoader Components
 * Provides shimmer loading placeholders for dashboard widgets
 */

// Base skeleton with theme-aware styling
const StyledSkeleton = ({ sx, ...props }) => {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  
  return (
    <Skeleton
      animation="wave"
      sx={{
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0, 0, 0, 0.06)",
        "&::after": {
          background: isDark
            ? "linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.08), transparent)"
            : "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
        },
        ...sx,
      }}
      {...props}
    />
  );
};

// KPI Card Skeleton
export const KPICardSkeleton = memo(function KPICardSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";

  return (
    <Box
      sx={{
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        padding: "20px",
        border: `1px solid ${borderColor}`,
        height: "160px",
      }}
    >
      <StyledSkeleton variant="rounded" width={48} height={48} sx={{ borderRadius: "12px", mb: 2 }} />
      <StyledSkeleton variant="text" width="60%" height={36} sx={{ mb: 0.5 }} />
      <StyledSkeleton variant="text" width="40%" height={20} />
    </Box>
  );
});

// Map Widget Skeleton
export const MapSkeleton = memo(function MapSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";

  return (
    <Box
      sx={{
        background: isDark ? "#021e2d" : "#f8fafc",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Map placeholder */}
      <StyledSkeleton variant="rectangular" width="100%" height="100%" />
      
      {/* Fake map markers */}
      <Box sx={{ position: "absolute", top: "30%", left: "40%" }}>
        <StyledSkeleton variant="circular" width={24} height={24} />
      </Box>
      <Box sx={{ position: "absolute", top: "50%", left: "60%" }}>
        <StyledSkeleton variant="circular" width={24} height={24} />
      </Box>
      <Box sx={{ position: "absolute", top: "40%", left: "25%" }}>
        <StyledSkeleton variant="circular" width={24} height={24} />
      </Box>
    </Box>
  );
});

// Widget Skeleton (for Activity Feed, Recent Events, etc.)
export const WidgetSkeleton = memo(function WidgetSkeleton({ rows = 5 }) {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";

  return (
    <Box
      sx={{
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ padding: "16px 20px", borderBottom: `1px solid ${borderColor}`, display: "flex", alignItems: "center", gap: 2 }}>
        <StyledSkeleton variant="rounded" width={40} height={40} sx={{ borderRadius: "10px" }} />
        <Box sx={{ flex: 1 }}>
          <StyledSkeleton variant="text" width="50%" height={20} />
          <StyledSkeleton variant="text" width="30%" height={14} />
        </Box>
      </Box>

      {/* List items */}
      <Box sx={{ padding: "12px" }}>
        {Array.from({ length: rows }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 8px",
            }}
          >
            <StyledSkeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <StyledSkeleton variant="text" width={`${70 - index * 5}%`} height={16} />
              <StyledSkeleton variant="text" width={`${40 + index * 3}%`} height={12} />
            </Box>
            <StyledSkeleton variant="text" width={50} height={12} />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

// Tracker Count Skeleton
export const TrackerCountSkeleton = memo(function TrackerCountSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";

  return (
    <Box
      sx={{
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        height: "100%",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
        <StyledSkeleton variant="rounded" width={36} height={36} sx={{ borderRadius: "10px" }} />
        <Box>
          <StyledSkeleton variant="text" width={100} height={20} />
          <StyledSkeleton variant="text" width={80} height={14} />
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "flex", gap: "12px", mb: 2 }}>
        <StyledSkeleton variant="rounded" width={100} height={60} sx={{ borderRadius: "10px" }} />
        <StyledSkeleton variant="rounded" width={100} height={60} sx={{ borderRadius: "10px" }} />
      </Box>

      {/* Pie chart placeholder */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <StyledSkeleton variant="circular" width={140} height={140} />
      </Box>

      {/* Total */}
      <StyledSkeleton variant="rounded" width={120} height={32} sx={{ borderRadius: "6px" }} />
    </Box>
  );
});

// Table/Grid Skeleton
export const TableSkeleton = memo(function TableSkeleton({ rows = 5, columns = 4 }) {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";

  return (
    <Box
      sx={{
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          padding: "16px 20px",
          borderBottom: `1px solid ${borderColor}`,
          gap: "16px",
        }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <StyledSkeleton
            key={index}
            variant="text"
            width={`${100 / columns}%`}
            height={20}
          />
        ))}
      </Box>

      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            padding: "14px 20px",
            borderBottom: `1px solid ${borderColor}`,
            gap: "16px",
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <StyledSkeleton
              key={colIndex}
              variant="text"
              width={`${100 / columns}%`}
              height={16}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
});

// Full Dashboard Skeleton
export const DashboardSkeleton = memo(function DashboardSkeleton() {
  return (
    <Box sx={{ padding: "24px" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mb: 3 }}>
        <StyledSkeleton variant="rounded" width={52} height={52} sx={{ borderRadius: "14px" }} />
        <Box>
          <StyledSkeleton variant="text" width={150} height={32} />
          <StyledSkeleton variant="text" width={200} height={20} />
        </Box>
      </Box>

      {/* KPI Cards */}
      <Box sx={{ display: "flex", gap: "24px", mb: 3 }}>
        <KPICardSkeleton />
        <KPICardSkeleton />
      </Box>

      {/* Map and Tracker */}
      <Box sx={{ display: "flex", gap: "24px", mb: 3 }}>
        <Box sx={{ flex: 2, height: "400px" }}>
          <MapSkeleton />
        </Box>
        <Box sx={{ flex: 1, height: "400px" }}>
          <TrackerCountSkeleton />
        </Box>
      </Box>

      {/* Widgets row */}
      <Box sx={{ display: "flex", gap: "24px", mb: 3 }}>
        <Box sx={{ flex: 1, height: "360px" }}>
          <WidgetSkeleton rows={4} />
        </Box>
        <Box sx={{ flex: 1, height: "360px" }}>
          <WidgetSkeleton rows={4} />
        </Box>
        <Box sx={{ flex: 1, height: "360px" }}>
          <WidgetSkeleton rows={4} />
        </Box>
      </Box>

      {/* Table */}
      <TableSkeleton rows={5} columns={5} />
    </Box>
  );
});

export default {
  KPICardSkeleton,
  MapSkeleton,
  WidgetSkeleton,
  TrackerCountSkeleton,
  TableSkeleton,
  DashboardSkeleton,
};
